import { BaseElement } from '../../base-element';
import { Signal } from 'signal-polyfill';
import { secretaryApi } from '../../../api/secretary.api';
import type { EntryDetailDto, EntryStatus, PdfStatus, ProcessingDto, TrialEventRegistrationConfigDto } from '../../../api/types';
import { trialsApi } from '../../../api/trials.api';
import { hasRegistrationActualTrialConfiguration } from '../../../api/actual-trials';
import { editRunSelections, runErrorControlPath, type RunSelectionDraft } from '../../../services/run-selections';
import type { ValidationError } from '../../../state/form.state';
import type { ActualRunsEditor } from '../registration/sections/actual-runs-editor';
import type { FeesSection } from '../registration/sections/fees-section';
import '../registration/sections/actual-runs-editor';
import '../registration/sections/fees-section';
import { navigate } from '../../../router';
import './components/processing-status-card';
import '../../ui/dt-card';
import '../../ui/dt-button';
import '../../ui/dt-badge';
import '../../ui/dt-spinner';

interface BadgeConfig {
  variant: 'success' | 'warning' | 'error' | 'info';
  label: string;
}

type PageState = 'loading' | 'loaded' | 'error';

type ProcessingCardElement = HTMLElement & {
  processing: ProcessingDto;
};

type DtButtonElement = HTMLElement & {
  disabled?: boolean;
  loading?: boolean;
};

export class SecretaryDetailPage extends BaseElement {
  private entryId = '';
  private trialEventId = '';
  private entry = new Signal.State<EntryDetailDto | null>(null);
  private loading = new Signal.State(false);
  private error = new Signal.State<string | null>(null);
  private isDownloading = new Signal.State(false);
  private config: TrialEventRegistrationConfigDto | null = null;
  private entryEtag: string | null = null;
  private draft: RunSelectionDraft = { actualHandlers: [], runs: [] };
  private editing = false;
  private saving = false;
  private regenerating = false;
  private note = '';
  private editError = '';
  private editErrors: ValidationError[] = [];
  private loadSequence = 0;

  private pageState = new Signal.Computed<PageState>(() => {
    if (this.loading.get()) return 'loading';
    if (this.error.get()) return 'error';
    return 'loaded';
  });

  static get observedAttributes(): string[] {
    return ['entry-id', 'trial-event-id'];
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    if (name === 'entry-id') {
      this.entryId = newValue || '';
      if (this.isConnected && this.trialEventId) {
        void this.loadEntry();
      }
    } else if (name === 'trial-event-id') {
      this.trialEventId = newValue || '';
      if (this.isConnected && this.entryId) {
        void this.loadEntry();
      }
    }
  }

  connectedCallback(): void {
    this.watch(this.entry, () => this.updateRender());
    this.watch(this.loading, () => this.updateRender());
    this.watch(this.error, () => this.updateRender());
    this.watch(this.isDownloading, () => this.updateRender());
    this.updateRender();

    if (this.entryId && this.trialEventId) {
      void this.loadEntry();
    }
  }

  private async loadEntry(): Promise<void> {
    if (!this.entryId || !this.trialEventId) {
      this.error.set('Missing entry or trial event identifier.');
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    const sequence = ++this.loadSequence;
    const [response, metadata] = await Promise.all([
      secretaryApi.getEntryDetail(this.trialEventId, this.entryId),
      trialsApi.getRegistrationMetadata(this.trialEventId),
    ]);
    if (!this.isConnected || sequence !== this.loadSequence) return;
    if (response.error || !response.data) {
      this.entry.set(null);
      this.error.set('Unable to load entry details.');
      this.loading.set(false);
      return;
    }

    this.config = metadata.data && hasRegistrationActualTrialConfiguration(metadata.data.trialConfig)
      ? metadata.data.trialConfig : null;
    this.entryEtag = response.etag ?? null;
    this.draft = editRunSelections(response.data.selections);
    this.editing = false;
    this.editErrors = [];
    this.note = '';
    this.editError = this.config && this.entryEtag ? '' : 'Current run configuration or entry ETag is unavailable. Reload before editing.';
    this.entry.set(response.data);
    this.loading.set(false);
  }

  private formatDate(value?: string | null, withTime = false): string {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
    });
  }

  private formatAddress(address?: {
    street?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
  } | null): string {
    if (!address) return '—';
    const parts = [address.street, address.city, address.state, address.zip].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '—';
  }

  private getStatusBadge(status: EntryStatus): BadgeConfig {
    if (status === 'Submitted') {
      return { variant: 'success', label: 'Submitted' };
    }
    return { variant: 'info', label: 'Draft' };
  }

  private getPdfBadge(status: PdfStatus): BadgeConfig {
    switch (status) {
      case 'Success':
        return { variant: 'success', label: 'Ready' };
      case 'Failed':
        return { variant: 'error', label: 'Failed' };
      case 'InProgress':
        return { variant: 'warning', label: 'Processing' };
      case 'Queued':
      default:
        return { variant: 'info', label: 'Queued' };
    }
  }

  private handleBack = (): void => {
    if (this.trialEventId) {
      navigate(`/secretary?trialEventId=${encodeURIComponent(this.trialEventId)}`);
      return;
    }

    navigate('/secretary');
  };

  private async saveRuns(): Promise<void> {
    if (this.saving || this.regenerating || !this.entryEtag || !this.config || !this.editing) return;
    this.editErrors = [];
    this.editError = '';
    if (!this.note.trim()) {
      this.editErrors = [{ field: 'noteContent', message: 'A correction note is required.' }];
      this.updateRender();
      this.querySelector<HTMLTextAreaElement>('[data-control-path="noteContent"]')?.focus();
      return;
    }
    const entryId = this.entryId;
    const sequence = this.loadSequence;
    this.saving = true;
    this.updateRender();
    try {
      const response = await secretaryApi.editEntry(this.trialEventId, entryId, {
        selections: structuredClone(this.draft), noteContent: this.note,
      }, this.entryEtag);
      if (!this.isConnected || sequence !== this.loadSequence || entryId !== this.entryId) return;
      if (response.error || !response.data || !response.etag) {
        this.editError = response.error?.status === 412
          ? 'Another edit changed this entry. Your candidate and note are retained. Reload and reconcile before saving again.'
          : response.error?.title ?? 'The server did not return the saved entry and ETag. Your candidate is retained.';
        this.editErrors = Object.entries(response.error?.errors ?? {}).flatMap(([field, messages]) =>
          messages.map(message => ({ field, message })));
        return;
      }
      this.entryEtag = response.etag;
      this.draft = editRunSelections(response.data.selections);
      this.note = '';
      this.editing = false;
      this.entry.set(response.data);
    } finally {
      this.saving = false;
      this.updateRender();
    }
  }

  private async downloadPdf(): Promise<void> {
    const entry = this.entry.get();
    if (!entry || this.isDownloading.get() || !this.trialEventId) return;

    this.isDownloading.set(true);
    const response = await secretaryApi.getPdfDownloadUrl(this.trialEventId, entry.entryId);
    this.isDownloading.set(false);

    if (response.data?.downloadUrl) {
      window.open(response.data.downloadUrl, '_blank');
    }
  }

  private async regeneratePdf(): Promise<void> {
    if (this.regenerating || this.editing || this.loading.get() || this.entry.get()?.status !== 'Submitted') return;
    const sequence = this.loadSequence;
    const entryId = this.entryId;
    const trialEventId = this.trialEventId;
    this.regenerating = true;
    this.updateRender();
    try {
      const response = await secretaryApi.regeneratePdf(trialEventId, entryId);
      if (!this.isConnected || sequence !== this.loadSequence ||
          entryId !== this.entryId || trialEventId !== this.trialEventId) return;
      if (response.error) {
        this.editError = response.error.title;
        return;
      }
      await this.loadEntry();
    } finally {
      this.regenerating = false;
      this.updateRender();
    }
  }

  private async retryPdf(): Promise<void> {
    const entry = this.entry.get();
    if (!entry || !this.trialEventId) return;

    const response = await secretaryApi.retryPdf(this.trialEventId, entry.entryId);
    if (response.error) return;

    this.entry.set({
      ...entry,
      processing: {
        ...entry.processing,
        pdfStatus: 'Queued',
      },
    });
  }

  private renderLoading(): string {
    return `
      <div class="overflow-hidden rounded-[28px] border border-neutral-200 bg-white shadow-[0_18px_48px_rgba(48,52,37,0.12)] dark:border-white/10 dark:bg-surface-dark" data-testid="loading-state">
        <div class="pointer-events-none absolute h-36 w-36 rounded-full bg-primary-100 blur-3xl dark:bg-primary-900/20"></div>
        <div class="relative flex min-h-[280px] flex-col items-center justify-center gap-4 px-6 text-center">
          <div class="inline-flex items-center rounded-full bg-primary-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-700 dark:bg-primary-500/15 dark:text-primary-200">Secretary entry detail</div>
          <dt-spinner size="large"></dt-spinner>
          <div class="space-y-2">
            <h2 class="text-2xl font-semibold text-neutral-900 dark:text-white">Loading entry detail</h2>
            <p class="text-sm text-neutral-600 dark:text-neutral-300">Fetching the handler submission, processing status, and PDF state.</p>
          </div>
        </div>
      </div>
    `;
  }

  private renderError(message: string): string {
    return `
      <div class="overflow-hidden rounded-[28px] border border-neutral-200 bg-white shadow-[0_18px_48px_rgba(48,52,37,0.12)] dark:border-white/10 dark:bg-surface-dark" data-testid="error-state">
        <div class="pointer-events-none absolute h-36 w-36 rounded-full bg-error-50 blur-3xl dark:bg-error-500/10"></div>
        <div class="relative flex min-h-[260px] flex-col items-center justify-center gap-4 px-6 text-center">
          <div class="flex h-16 w-16 items-center justify-center rounded-full bg-error-50 text-error-500 ring-8 ring-white dark:bg-error-500/10 dark:ring-surface-dark">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-8 w-8">
              <path fill-rule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div class="space-y-2">
            <h2 class="text-2xl font-semibold text-neutral-900 dark:text-white">Unable to load entry</h2>
            <p class="max-w-md text-sm text-neutral-600 dark:text-neutral-300">${message}</p>
          </div>
        </div>
      </div>
    `;
  }

  private renderDetail(entry: EntryDetailDto): string {
    const statusBadge = this.getStatusBadge(entry.status);
    const pdfBadge = this.getPdfBadge(entry.processing.pdfStatus as PdfStatus);

    return `
      <div class="flex flex-col gap-6" data-testid="detail-content">
        <dt-card variant="outlined">
          <div slot="header" class="flex flex-wrap items-center justify-between gap-3">
            <span>Entry overview</span>
            <div class="flex flex-wrap items-center gap-2">
              <dt-badge variant="${statusBadge.variant}">${statusBadge.label}</dt-badge>
              <dt-badge variant="${pdfBadge.variant}">PDF ${pdfBadge.label}</dt-badge>
            </div>
          </div>
          <div class="grid gap-4 lg:grid-cols-[1.4fr_0.6fr] lg:items-end">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-secondary-600 dark:text-secondary-300">Handler submission</p>
              <h1 class="mt-3 text-3xl font-semibold text-neutral-900 dark:text-white">${entry.dog.callName ?? 'Entry detail'}</h1>
              <p class="mt-3 max-w-2xl text-sm leading-7 text-neutral-600 dark:text-neutral-300">Review the submitted entry, download the generated PDF when ready, and track processing from one place.</p>
            </div>
            <div class="grid gap-2 rounded-2xl border border-neutral-200 bg-neutral-50 p-4 text-sm shadow-xs dark:border-white/10 dark:bg-neutral-950/40">
              <div><span class="font-semibold text-neutral-700 dark:text-neutral-200">Trial:</span> <span data-testid="overview-entry-trial">${entry.trialEvent?.name ?? '—'}</span></div>
              <div><span class="font-semibold text-neutral-700 dark:text-neutral-200">Entry #:</span> <span data-testid="overview-entry-number">${entry.entryNumber ?? '—'}</span></div>
              <div><span class="font-semibold text-neutral-700 dark:text-neutral-200">Submitted:</span> <span data-testid="overview-entry-submitted">${this.formatDate(entry.submittedAtUtc, true)}</span></div>
            </div>
          </div>
        </dt-card>

        <dt-card variant="outlined">
          <div slot="header" class="flex flex-wrap items-center justify-between gap-3">
            <span>Entry Detail</span>
            <dt-badge variant="${statusBadge.variant}">${statusBadge.label}</dt-badge>
          </div>
          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Entry ID</div>
              <div class="font-mono text-xs text-neutral-700" data-testid="entry-id">${entry.entryId}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Entry #</div>
              <div class="text-sm font-semibold text-neutral-800" data-testid="entry-number">${entry.entryNumber ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Submitted</div>
              <div class="text-sm text-neutral-700" data-testid="entry-submitted">${this.formatDate(entry.submittedAtUtc, true)}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Trial</div>
              <div class="text-sm text-neutral-700" data-testid="entry-trial">${entry.trialEvent?.name ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Handler Email</div>
              <div class="text-sm text-neutral-700" data-testid="entry-handler-email">${entry.contact.handlerEmail ?? entry.createdForUserEmail ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Owner Email</div>
              <div class="text-sm text-neutral-700" data-testid="entry-owner-email">${entry.contact.ownerEmail ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">PDF Status</div>
              <dt-badge variant="${pdfBadge.variant}">${pdfBadge.label}</dt-badge>
            </div>
          </div>
        </dt-card>

        <dt-card variant="outlined">
          <div slot="header">Dog Information</div>
          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Call Name</div>
              <div class="text-sm font-semibold text-neutral-800" data-testid="dog-call">${entry.dog.callName ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Registered Name</div>
              <div class="text-sm text-neutral-700">${entry.dog.registeredName ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">ASCA Reg #</div>
              <div class="text-sm text-neutral-700">${entry.dog.ascaRegistrationNumber ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Breed</div>
              <div class="text-sm text-neutral-700">${entry.dog.breed ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">DOB</div>
              <div class="text-sm text-neutral-700">${entry.dog.dob ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Sex</div>
              <div class="text-sm text-neutral-700">${entry.dog.sex ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Color</div>
              <div class="text-sm text-neutral-700">${entry.dog.color ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Sire</div>
              <div class="text-sm text-neutral-700">${entry.dog.sire ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Dam</div>
              <div class="text-sm text-neutral-700">${entry.dog.dam ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Breeders</div>
              <div class="text-sm text-neutral-700">${entry.dog.breeders ?? '—'}</div>
            </div>
          </div>
        </dt-card>

        <dt-card variant="outlined">
          <div slot="header">Contact / Emergency / Fees</div>
          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Owners</div>
              <div class="text-sm text-neutral-700">${entry.contact.owners ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Owner Email</div>
              <div class="text-sm text-neutral-700" data-testid="contact-owner-email">${entry.contact.ownerEmail ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Handler Email</div>
              <div class="text-sm text-neutral-700" data-testid="contact-handler-email">${entry.contact.handlerEmail ?? entry.createdForUserEmail ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Phone</div>
              <div class="text-sm text-neutral-700">${entry.contact.phone ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Handler</div>
              <div class="text-sm text-neutral-700">${entry.contact.handler ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Membership #</div>
              <div class="text-sm text-neutral-700">${entry.contact.membershipNumber ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Owner Address</div>
              <div class="text-sm text-neutral-700">${this.formatAddress(entry.contact.ownerAddress)}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Junior</div>
              <div class="text-sm text-neutral-700">${entry.contact.junior?.memberId ?? '—'}${entry.contact.junior?.dob ? ` (${entry.contact.junior?.dob})` : ''}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Emergency Contact</div>
              <div class="text-sm text-neutral-700">${entry.emergencyContact.name ?? '—'}${entry.emergencyContact.phoneOrNumber ? ` (${entry.emergencyContact.phoneOrNumber})` : ''}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Total Fees</div>
              <div class="text-sm text-neutral-700">${entry.fees.totalEntryFees ?? '—'} ${entry.fees.currency ?? ''}</div>
            </div>
          </div>
        </dt-card>

        <dt-card variant="outlined">
          ${this.editError || this.editErrors.length ? `<div role="alert" class="grid gap-2 break-words text-error-700">
            <p>${this.esc(this.editError)}</p>
            ${this.editErrors.map((error, index) => `<button type="button" class="text-left underline" data-edit-error="${index}">${this.esc(error.field)}: ${this.esc(error.message)}</button>`).join('')}
          </div>` : ''}
          <dt-actual-runs-editor tabindex="-1"></dt-actual-runs-editor>
          <dt-fees-section></dt-fees-section>
          ${this.editing ? `
            <label class="grid gap-2">Correction note (required)
              <textarea class="w-full rounded-lg border border-gray-200 p-2" maxlength="2000" data-control-path="noteContent"
                ${this.saving ? 'disabled' : ''}>${this.esc(this.note)}</textarea>
            </label>
            <div class="mt-3 flex flex-wrap gap-3">
              <button type="button" class="rounded-lg border border-gray-200 p-2" data-save-runs ${this.saving ? 'disabled' : ''}>Save run correction</button>
              <button type="button" class="rounded-lg border border-gray-200 p-2" data-cancel-runs ${this.saving ? 'disabled' : ''}>Cancel correction</button>
            </div>
          ` : `<button type="button" class="mt-3 rounded-lg border border-gray-200 p-2" data-edit-runs
            ${this.regenerating || !this.config || !this.entryEtag ? 'disabled' : ''}>Edit actual runs</button>`}
          ${this.regenerating ? '<p role="status">PDF regeneration is in progress. Run corrections are available after the entry refresh completes.</p>' : ''}
          <p class="mt-3 text-sm">Entry revision ${entry.revision}. Accepted substantive corrections require PDF regeneration;
            rejected candidates do not create a correction note or invalidate the saved packet.</p>
        </dt-card>

        <div class="flex flex-wrap items-center gap-3">
          ${entry.status === 'Submitted' ? `<button type="button" class="rounded-lg border border-gray-200 p-2" data-regenerate-pdf
            ${this.editing || this.regenerating || entry.processing.pdfStatus === 'InProgress' ? 'disabled' : ''}>Regenerate PDF</button>` : ''}
          <dt-button
            variant="primary"
            data-testid="download-pdf"
          >Download PDF</dt-button>
          ${entry.processing.pdfStatus === 'Failed'
            ? '<dt-button variant="outline" data-testid="retry-pdf">Retry PDF</dt-button>'
            : ''}
        </div>

        <dt-processing-status-card data-testid="processing-status"></dt-processing-status-card>
      </div>
    `;
  }

  private updateRender(): void {
    const restoreFocus = this.retainControlFocus();
    const pageState = this.pageState.get();
    let content = '';

    if (pageState === 'loading') {
      content = this.renderLoading();
    } else if (pageState === 'error') {
      content = this.renderError(this.error.get() ?? 'Unable to load.');
    } else {
      const entry = this.entry.get();
      if (entry) {
        content = this.renderDetail(entry);
      }
    }

    this.render(`
      <section class="px-4 py-6" data-testid="secretary-detail-page">
        <div class="mx-auto flex w-full max-w-5xl flex-col gap-6">
          <div class="flex items-center justify-start">
            <dt-button variant="ghost" data-testid="back-btn">Back to list</dt-button>
          </div>
          ${content}
        </div>
      </section>
    `);

    this.attachEventListeners();
    this.assignData();
    restoreFocus();
  }

  private attachEventListeners(): void {
    this.querySelector('[data-edit-runs]')?.addEventListener('click', () => {
      if (this.regenerating || this.saving || this.loading.get() || !this.config || !this.entryEtag) return;
      this.editing = true;
      this.updateRender();
    });
    this.querySelector('[data-cancel-runs]')?.addEventListener('click', () => {
      const entry = this.entry.get();
      if (entry) this.draft = editRunSelections(entry.selections);
      this.editing = false; this.note = ''; this.editError = ''; this.editErrors = [];
      this.updateRender();
    });
    this.querySelector('[data-save-runs]')?.addEventListener('click', () => void this.saveRuns());
    this.querySelector('[data-regenerate-pdf]')?.addEventListener('click', () => void this.regeneratePdf());
    this.querySelector<HTMLTextAreaElement>('[data-control-path="noteContent"]')?.addEventListener('input', event => {
      this.note = (event.target as HTMLTextAreaElement).value;
    });
    this.querySelector('dt-actual-runs-editor')?.addEventListener('runs-change', event => {
      this.draft = (event as CustomEvent<RunSelectionDraft>).detail;
      this.editError = ''; this.editErrors = [];
      this.updateRender();
    });
    this.querySelectorAll<HTMLButtonElement>('[data-edit-error]').forEach(button =>
      button.addEventListener('click', () => {
        const path = runErrorControlPath(this.editErrors[Number(button.dataset.editError)].field);
        const target = this.querySelector<HTMLElement>(`[data-control-path="${path}"]`) ??
          this.querySelector<HTMLElement>('dt-actual-runs-editor');
        target?.focus();
      }));
    const backBtn = this.$('[data-testid="back-btn"]');
    if (backBtn) {
      backBtn.addEventListener('click', this.handleBack);
    }

    const downloadBtn = this.$('[data-testid="download-pdf"]');
    if (downloadBtn) {
      downloadBtn.addEventListener('click', () => void this.downloadPdf());
    }

    const retryBtn = this.$('[data-testid="retry-pdf"]');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => void this.retryPdf());
    }
  }

  private assignData(): void {
    const entry = this.entry.get();
    if (!entry) return;
    const quote = !this.editing && entry.pricingQuote?.configurationVersion === this.config?.configurationVersion &&
      entry.pricingQuote?.entryRevision === entry.revision ? entry.pricingQuote : null;
    const editor = this.querySelector<ActualRunsEditor>('dt-actual-runs-editor');
    if (editor) editor.data = {
      config: this.config, selections: this.draft, quote,
      errors: this.editErrors.map(error => ({ ...error, field: runErrorControlPath(error.field) })),
      disabled: !this.editing || this.saving,
    };
    const fees = this.querySelector<FeesSection>('dt-fees-section');
    if (fees) { fees.quote = quote; fees.config = this.config; }

    const processingCard = this.$('dt-processing-status-card') as ProcessingCardElement | null;
    if (processingCard) {
      processingCard.processing = entry.processing;
    }

    const downloadBtn = this.$('[data-testid="download-pdf"]') as DtButtonElement | null;
    if (downloadBtn) {
      downloadBtn.disabled = entry.processing.pdfStatus !== 'Success' || this.isDownloading.get();
      downloadBtn.loading = this.isDownloading.get();
    }
  }
}

customElements.define('dt-secretary-detail-page', SecretaryDetailPage);
